import * as AccordionPrimitive from '@radix-ui/react-accordion';
import React from 'react';
import { clsx } from 'clsx';
import styles from './Accordion.module.css';
import { Icon } from '../../foundations';

export type AccordionProps = React.ComponentProps<typeof AccordionPrimitive.Root> & {
	/**
	 * The class name to add to the item. With header and content.
	 */
	className?: string;
	/**
	 * The children to render.
	 */
	children?: React.ReactNode;
};

const AccordionRoot = React.forwardRef<React.ElementRef<typeof AccordionPrimitive.Root>, AccordionProps>(
	function AccordionItem({ children, className, ...props }, ref) {
		const defaultProps = {
			collapsible: true
		};
		return (
			<AccordionPrimitive.Root ref={ref} {...defaultProps} {...props} className={clsx(styles.accordionRoot, className)}>
				{children}
			</AccordionPrimitive.Root>
		);
	}
);
AccordionRoot.displayName = 'AccordionRoot';

export type AccordionItemProps = React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item> & {
	/**
	 * The class name to add to the item. With header and content.
	 */
	className?: string;
	/**
	 * The children to render.
	 */
	children: React.ReactNode;
};

const AccordionItem = React.forwardRef<React.ElementRef<typeof AccordionPrimitive.Item>, AccordionItemProps>(
	function AccordionItem({ className, children, ...props }, ref) {
		return (
			<AccordionPrimitive.Item ref={ref} className={clsx(styles.accordionItem, className)} {...props}>
				{children}
			</AccordionPrimitive.Item>
		);
	}
);
AccordionItem.displayName = 'AccordionItem';

export type AccordionTriggerProps = React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & {
	/**
	 * The class name to add to the header.
	 */
	className?: string;
	/**
	 * The children to render.
	 */
	children?: React.ReactNode;
};

const AccordionTrigger = React.forwardRef<React.ElementRef<typeof AccordionPrimitive.Trigger>, AccordionTriggerProps>(
	function AccordionTrigger({ className, children, ...props }, ref) {
		return (
			<AccordionPrimitive.Trigger ref={ref} className={clsx(styles.accordionTrigger, className)} {...props}>
				{children}
			</AccordionPrimitive.Trigger>
		);
	}
);
AccordionTrigger.displayName = 'AccordionTrigger';

export type AccordionHeaderProps = React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Header> & {
	/**
	 * If show the default Iberia trigger.
	 * @default true
	 */
	showTrigger?: boolean;
	/**
	 * The class name to add to the header.
	 */
	className?: string;
	/**
	 * The children to render.
	 */
	children: React.ReactNode;
};

const AccordionHeader = React.forwardRef<React.ElementRef<typeof AccordionPrimitive.Header>, AccordionHeaderProps>(
	function AccordionHeader({ className, children, showTrigger = true, ...props }, ref) {
		return (
			<AccordionPrimitive.Header ref={ref} className={clsx(styles.accordionHeader, className)} {...props}>
				{showTrigger ? (
					<AccordionTrigger className={clsx(styles.accordionTrigger, className)}>
						{children}
						<Icon name="chevron-line-down" className={styles.accordionHeaderIcon} />
					</AccordionTrigger>
				) : (
					children
				)}
			</AccordionPrimitive.Header>
		);
	}
);
AccordionHeader.displayName = 'AccordionHeader';

export type AccordionContentProps = React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content> & {
	/**
	 * The class name to add to the content collapsible.
	 */
	className?: string;
	/**
	 * The children to render.
	 */
	children: React.ReactNode;
};

const AccordionContent = React.forwardRef<React.ElementRef<typeof AccordionPrimitive.Content>, AccordionContentProps>(
	function AccordionContent({ className, children, ...props }, ref) {
		return (
			<AccordionPrimitive.Content ref={ref} className={clsx(styles.accordionContent, className)} {...props}>
				<div className={styles.accordionContentContainer}>{children}</div>
			</AccordionPrimitive.Content>
		);
	}
);
AccordionContent.displayName = 'AccordionContent';
/* Compound pattern */
export const Accordion = Object.assign(AccordionRoot, {
	Content: AccordionContent,
	Item: AccordionItem,
	Header: AccordionHeader,
	Trigger: AccordionTrigger
});
